import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { SERVICES } from "@/data/services";

const SLIDE_MS = 6500;

// 13 services split into 4 logical groups of 4 (last group has 1 + CTA tiles)
const SERVICE_GROUPS = [
  {
    eyebrow: "Services · Group 01 / 04",
    title: "Boundary, title",
    titleAccent: "& topo.",
    intro:
      "Property lines, ALTA/NSPS, subdivision platting, and topographic base maps — drafted to your jurisdiction.",
    slugs: ["boundary-survey", "alta-nsps-land-title-survey", "topographic-survey", "subdivision-platting"],
  },
  {
    eyebrow: "Services · Group 02 / 04",
    title: "Construction",
    titleAccent: "& corridor.",
    intro:
      "As-built, construction staking, right-of-way and FEMA elevation certificates — on time, on standard.",
    slugs: ["as-built-survey", "construction-staking-drafting", "route-right-of-way-survey", "fema-elevation-certificate"],
  },
  {
    eyebrow: "Services · Group 03 / 04",
    title: "Specialty",
    titleAccent: "& exhibits.",
    intro:
      "Bathymetric, residential plot plans, volumetric reports, and recordable legal descriptions with exhibits.",
    slugs: ["hydrographic-bathymetric-drafting", "mortgage-location-plot-plan", "volumetric-earthwork", "legal-description-exhibits"],
  },
  {
    eyebrow: "Services · Group 04 / 04",
    title: "GIS data,",
    titleAccent: "drafted clean.",
    intro:
      "CAD ↔ GIS conversion, attribute cleanup, geodatabase construction, and thematic map drafting.",
    slugs: ["gis-drafting"],
    // last group has < 4 services — fill with action tiles
    fill: [
      { kind: "cta", to: "/services", label: "View all 13 services", variant: "dark" },
      { kind: "cta", to: "/free-trial", label: "Start a free trial", variant: "blue" },
      { kind: "cta", to: "/contact", label: "Talk to a drafter", variant: "ghost" },
    ],
  },
];

export default function HeroSlider() {
  const slides = [
    { kind: "original" },
    ...SERVICE_GROUPS.map((g) => ({ kind: "services", group: g })),
    { kind: "coverage" },
  ];
  const total = slides.length;

  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (paused) return;
    timer.current = setTimeout(() => setI((x) => (x + 1) % total), SLIDE_MS);
    return () => clearTimeout(timer.current);
  }, [i, paused, total]);

  return (
    <section
      className="relative overflow-hidden bg-zinc-50 ruler-top"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="hero-slider"
    >
      <div className="absolute inset-0 bg-blueprint-grid pointer-events-none" aria-hidden="true" />
      <div className="relative">
        <div className="relative">
          {slides.map((slide, idx) => (
            <div
              key={idx}
              className={`transition-opacity duration-700 ${
                idx === i ? "opacity-100 relative z-10" : "opacity-0 absolute inset-0 z-0 pointer-events-none"
              }`}
              aria-hidden={idx !== i}
              data-testid={`hero-slide-${idx}`}
            >
              {slide.kind === "original" && <SlideOriginal />}
              {slide.kind === "services" && <SlideServices group={slide.group} />}
              {slide.kind === "coverage" && <SlideCoverage />}
            </div>
          ))}
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-6 md:px-10 pb-8 flex items-center gap-3 flex-wrap">
          {slides.map((_, n) => (
            <button
              key={n}
              onClick={() => setI(n)}
              aria-label={`Go to slide ${n + 1}`}
              data-testid={`hero-dot-${n}`}
              className={`h-1.5 transition-all ${
                n === i ? "w-12 bg-[#0044FF]" : "w-6 bg-zinc-300 hover:bg-zinc-500"
              }`}
            />
          ))}
          <span className="ml-2 text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500">
            {String(i + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </span>
          {paused && (
            <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#0044FF]">paused</span>
          )}
        </div>
      </div>
    </section>
  );
}

/* ---------- Slide: original hero (unchanged) ---------- */
function SlideOriginal() {
  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-16 md:pb-24">
      <div className="grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8 fade-up">
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-block w-2 h-2 bg-[#0044FF]" />
            <span className="text-[11px] font-mono uppercase tracking-[0.3em] text-zinc-600">
              Land Surveying CAD Drafting · USA · All 50 States
            </span>
          </div>
          <h1
            className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[92px] tracking-tighter font-medium leading-[0.95] text-zinc-950 text-balance"
            data-testid="hero-headline"
          >
            Draft-ready surveys.<br />
            <span className="text-[#0044FF]">Jurisdiction-accurate.</span><br />
            Coast to coast.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-zinc-600 leading-relaxed">
            cad4surveys is a dedicated CAD drafting partner for licensed land surveyors,
            civil engineering firms, and developers across the United States.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="group px-6 py-4 bg-[#0044FF] hover:bg-[#0033CC] text-white font-medium inline-flex items-center gap-2 btn-lift"
              data-testid="hero-cta-get-started"
            >
              Get Started <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/free-trial"
              className="px-6 py-4 border border-zinc-900 text-zinc-950 font-medium inline-flex items-center gap-2 btn-lift hover:bg-zinc-900 hover:text-white"
              data-testid="hero-cta-free-trial"
            >
              Start a Free Trial
            </Link>
          </div>
        </div>

        <div className="lg:col-span-4">
          <div className="border border-zinc-900 bg-white p-6 md:p-8 font-mono text-xs">
            <div className="flex justify-between text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4 pb-4 border-b border-dashed border-zinc-300">
              <span>Service Brief</span>
              <span>FIG.01</span>
            </div>
            <dl className="space-y-3">
              <div className="flex justify-between"><dt className="text-zinc-500">Datum</dt><dd className="text-zinc-900">NAD83 / NAVD88</dd></div>
              <div className="flex justify-between"><dt className="text-zinc-500">Instruments</dt><dd className="text-zinc-900">Trimble · Leica · Topcon</dd></div>
              <div className="flex justify-between"><dt className="text-zinc-500">CAD</dt><dd className="text-zinc-900">AutoCAD · Civil 3D</dd></div>
              <div className="flex justify-between"><dt className="text-zinc-500">Turnaround</dt><dd className="text-zinc-900">24–72 hrs typical</dd></div>
              <div className="flex justify-between"><dt className="text-zinc-500">Coverage</dt><dd className="text-zinc-900">50 States</dd></div>
              <div className="flex justify-between"><dt className="text-zinc-500">Team</dt><dd className="text-zinc-900">Surveyor-trained</dd></div>
            </dl>
            <div className="mt-6 pt-4 border-t border-dashed border-zinc-300 flex items-center gap-2 text-zinc-700">
              <CheckCircle size={14} weight="fill" className="text-[#0044FF]" />
              <span>Free sample drafting on first project</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Slide: 4 services per slide (2x2 grid) ---------- */
function SlideServices({ group }) {
  const services = group.slugs
    .map((slug) => {
      const idx = SERVICES.findIndex((s) => s.slug === slug);
      return idx >= 0 ? { ...SERVICES[idx], number: idx + 1 } : null;
    })
    .filter(Boolean);

  // Build cells: services first, then any "fill" tiles, capped at 4
  const cells = [
    ...services.map((s) => ({ kind: "service", svc: s })),
    ...(group.fill || []).map((c) => ({ kind: "fill", cta: c })),
  ].slice(0, 4);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-16 md:pb-24">
      <div className="grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-block w-2 h-2 bg-[#0044FF]" />
            <span className="text-[11px] font-mono uppercase tracking-[0.3em] text-zinc-600">
              {group.eyebrow}
            </span>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-[80px] tracking-tighter font-medium leading-[0.95] text-zinc-950 text-balance">
            {group.title}<br />
            <span className="text-[#0044FF]">{group.titleAccent}</span>
          </h1>
          <p className="mt-6 max-w-md text-lg text-zinc-600 leading-relaxed">
            {group.intro}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/services"
              className="px-6 py-4 bg-zinc-950 hover:bg-zinc-800 text-white font-medium inline-flex items-center gap-2 btn-lift"
            >
              All 13 services <ArrowRight size={14} weight="bold" />
            </Link>
            <Link
              to="/free-trial"
              className="px-6 py-4 border border-zinc-900 text-zinc-950 font-medium inline-flex items-center gap-2 btn-lift hover:bg-zinc-900 hover:text-white"
            >
              Free Trial
            </Link>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
            {cells.map((cell, idx) =>
              cell.kind === "service" ? (
                <Link
                  key={cell.svc.slug}
                  to={`/services/${cell.svc.slug}`}
                  className="bg-white p-6 hover:bg-zinc-50 group min-h-[180px] flex flex-col justify-between"
                  data-testid={`hero-svc-${cell.svc.slug}`}
                >
                  <div className="flex items-start justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                      {String(cell.svc.number).padStart(2, "0")} / 13
                    </span>
                    <ArrowRight
                      size={14}
                      weight="bold"
                      className="text-zinc-300 group-hover:text-[#0044FF] group-hover:translate-x-1 transition-all"
                    />
                  </div>
                  <div>
                    <div className="font-display text-xl text-zinc-950 leading-tight tracking-tight">
                      {cell.svc.title}
                    </div>
                    <div className="mt-2 text-xs text-zinc-600 leading-relaxed line-clamp-2">
                      {cell.svc.short}
                    </div>
                  </div>
                </Link>
              ) : (
                <FillTile key={`fill-${idx}`} cta={cell.cta} />
              ),
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FillTile({ cta }) {
  const map = {
    dark: "bg-zinc-950 text-white hover:bg-zinc-800",
    blue: "bg-[#0044FF] text-white hover:bg-[#0033CC]",
    ghost: "bg-white text-zinc-950 hover:bg-zinc-50",
  };
  const accent = cta.variant === "ghost" ? "text-zinc-400" : "text-white/60";
  return (
    <Link
      to={cta.to}
      className={`p-6 min-h-[180px] flex flex-col justify-between transition-colors ${map[cta.variant] || map.dark}`}
      data-testid={`hero-fill-${cta.label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <span className={`font-mono text-[10px] uppercase tracking-[0.25em] ${accent}`}>
        Next step →
      </span>
      <div className="flex items-end justify-between gap-2">
        <div className="font-display text-xl leading-tight tracking-tight">{cta.label}</div>
        <ArrowRight size={18} weight="bold" />
      </div>
    </Link>
  );
}

/* ---------- Slide: USA coverage ---------- */
function SlideCoverage() {
  const STATES = [
    "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
    "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
    "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
  ];
  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-16 md:pb-24">
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-block w-2 h-2 bg-[#0044FF]" />
            <span className="text-[11px] font-mono uppercase tracking-[0.3em] text-zinc-600">
              Coverage · USA · All 50 States
            </span>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-[80px] tracking-tighter font-medium leading-[0.95] text-zinc-950 text-balance">
            From Anchorage<br />
            <span className="text-[#0044FF]">to Key West.</span>
          </h1>
          <p className="mt-6 max-w-md text-lg text-zinc-600 leading-relaxed">
            Versed in state-specific standards — Texas GLO calls, New England rod-and-link
            descriptions, Louisiana civil law boundaries.
          </p>
          <Link
            to="/contact"
            className="mt-8 inline-flex items-center gap-2 px-6 py-4 bg-[#0044FF] hover:bg-[#0033CC] text-white font-medium btn-lift"
            data-testid="hero-coverage-cta"
          >
            Tell us your state <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
        <div className="lg:col-span-6">
          <div className="border border-zinc-900 p-5 bg-white">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.25em] font-mono text-zinc-500 pb-3 mb-3 border-b border-dashed border-zinc-300">
              <span>USA · 50 States Grid</span>
              <span>FIG.02</span>
            </div>
            <div className="grid grid-cols-10 gap-1.5">
              {STATES.map((s) => (
                <div
                  key={s}
                  className="aspect-square border border-zinc-300 flex items-center justify-center text-[9px] font-mono text-zinc-500 hover:bg-[#0044FF] hover:text-white hover:border-[#0044FF] transition-colors"
                  title={s}
                >
                  {s}
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-dashed border-zinc-300 text-xs font-mono text-zinc-500">
              50 / 50 states · Alaska & Hawaii included
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
